import { useMemo } from "react";
import { geoNaturalEarth1, geoPath, geoInterpolate, type GeoPermissibleObjects } from "d3-geo";
import { feature } from "topojson-client";
import type { Topology, GeometryCollection } from "topojson-specification";
import worldLand from "world-atlas/land-110m.json";
import { formatDistanceKm } from "@hunch/core";
import styles from "./CrowflightMap.module.css";

export interface MapPoint {
  id: number;
  lat: number;
  lon: number;
  name: string;
}

export interface CrowflightMapProps {
  /** Reference point, with great-circle arcs drawn to each `point`. Omit for latitude mode. */
  reference?: MapPoint;
  points: MapPoint[];
  mode: "arcs" | "latitude";
  distancesKm?: Record<number, number>;
  lang: "de" | "en";
}

const WIDTH = 600;
const HEIGHT = 360;
const PADDING = 28;

const landTopology = worldLand as unknown as Topology;
const landGeo = feature(
  landTopology,
  landTopology.objects["land"] as GeometryCollection,
) as unknown as GeoPermissibleObjects;

export function CrowflightMap({ reference, points, mode, distancesKm, lang }: CrowflightMapProps) {
  const { landPath, markers, arcs, latLines } = useMemo(() => {
    const allPoints = reference ? [reference, ...points] : points;
    const pointsGeoJson: GeoPermissibleObjects = {
      type: "FeatureCollection",
      features: allPoints.map((p) => ({
        type: "Feature",
        properties: {},
        geometry: { type: "Point", coordinates: [p.lon, p.lat] },
      })),
    } as never;

    // A single point has a zero-area bounding box, which makes fitExtent
    // degenerate (effectively infinite scale, nothing renders) — fit to the
    // whole world instead in that case (e.g. the "give hint" map, which
    // only ever shows one place with no reference).
    const fitTarget = allPoints.length > 1 ? pointsGeoJson : landGeo;
    const projection = geoNaturalEarth1().fitExtent(
      [
        [PADDING, PADDING],
        [WIDTH - PADDING, HEIGHT - PADDING],
      ],
      fitTarget,
    );
    const path = geoPath(projection);

    const landPath = path(landGeo) ?? "";

    const markers = allPoints.map((p) => {
      const [x, y] = projection([p.lon, p.lat]) ?? [0, 0];
      return { id: p.id, x, y, name: p.name, isReference: reference?.id === p.id };
    });

    let arcs: Array<{ id: number; d: string; labelX: number; labelY: number; label: string }> = [];
    if (mode === "arcs" && reference) {
      arcs = points.map((p) => {
        const interpolate = geoInterpolate([reference.lon, reference.lat], [p.lon, p.lat]);
        const samples = 40;
        const coords = Array.from({ length: samples + 1 }, (_, i) => interpolate(i / samples));
        const lineGeo: GeoPermissibleObjects = {
          type: "LineString",
          coordinates: coords,
        } as never;
        const d = path(lineGeo) ?? "";
        const mid = interpolate(0.5);
        const [mx, my] = projection(mid) ?? [0, 0];
        const km = distancesKm?.[p.id];
        const label = km !== undefined ? formatDistanceKm(km, lang) + " km" : "";
        return { id: p.id, d, labelX: mx, labelY: my, label };
      });
    }

    let latLines: Array<{ id: number; y: number; label: string }> = [];
    if (mode === "latitude") {
      latLines = points.map((p) => {
        const [, y] = projection([p.lon, p.lat]) ?? [0, 0];
        return { id: p.id, y, label: p.name };
      });
    }

    return { landPath, markers, arcs, latLines };
  }, [reference, points, mode, distancesKm, lang]);

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className={styles.map}
      role="img"
      aria-label={lang === "de" ? "Kartenausschnitt mit den Orten dieser Runde" : "Map of this round's places"}
    >
      <rect x={0} y={0} width={WIDTH} height={HEIGHT} className={styles.sea} />
      <path d={landPath} className={styles.land} />

      {mode === "latitude" &&
        latLines.map((l) => (
          <line key={l.id} x1={PADDING} x2={WIDTH - PADDING} y1={l.y} y2={l.y} className={styles.latLine} />
        ))}

      {mode === "arcs" &&
        arcs.map((a) => (
          <g key={a.id}>
            <path d={a.d} className={styles.arc} />
            {a.label && (
              <text x={a.labelX} y={a.labelY - 4} className={styles.arcLabel} textAnchor="middle">
                {a.label}
              </text>
            )}
          </g>
        ))}

      {markers.map((m) => (
        <g key={m.id}>
          <circle cx={m.x} cy={m.y} r={m.isReference ? 6 : 4.5} className={m.isReference ? styles.refMarker : styles.marker} />
          <text x={m.x} y={m.y - 10} className={styles.markerLabel} textAnchor="middle">
            {m.name}
          </text>
        </g>
      ))}
    </svg>
  );
}
